import { useRef, useState, useEffect, useContext } from "react";
import { useReactFlow } from "reactflow";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import FormField from "layouts/Flow Designer/components/FormField";
import Box from "@mui/material/Box";
import TriggerFormWrapper from "../TriggerFormWrapper";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import validations from "./schemas/validations";
import schedule from "./schemas/schedule";
import FormTabs from "../FormTabs";
import { openDiscardDialog } from "redux/actions/flowDesigner/commonActionCreator";
import Comments from "layouts/Flow Designer/components/FlowPropertiesPanel/components/Comments";
import { useSchedules } from "hooks/useSchedules";
import { format } from "date-fns";
import { updateFlowTrigger, clearSelectedTrigger } from "redux/actions/flowDesigner/triggersActionCreator";
import { updateContextTrigger } from "redux/actions/flowDesigner/editorContextActionCreator";
import { TabContextApi } from "layouts/Flow Designer/context";
import initialValues from "./schemas/initialValues";
import { insertScheduleByFlow } from "api/FlowDesignerApis";
import { toast } from "react-toastify";
import {
   createResourceDependencies,
   deleteResourceDependencies
} from "api/ResourceDependenciesApi";

const TabPanel = ({ children, value = 0, index = 0 }) => {
   return <>{value === index && children}</>;
};

const normalizeScheduleIds = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const ScheduleTriggerForm = ({ selectedTrigger, allowEditWorkflow, handleGoBackToStartNodePanel }) => {
   const { showTriggerPropertiesPanel } = useSelector((store) => store?.designer?.triggers);
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const [submitting, setSubmitting] = useState(false);
   const formRef = useRef(null);
   const [tabValue, setTabValue] = useState(0);
   const { autoSaveFlow } = useContext(TabContextApi);
   const reactFlowInstance = useReactFlow();
   const dispatch = useDispatch();
   const {
      formId,
      formField: { schedules, notes }
   } = schedule;
   const { ownedSharedSchedules } = useSchedules();
   const originalScheduleIdsRef = useRef([]);

   const selectedTriggerProps = selectedTrigger?.properties;

   const isFlowActive = currentSavedFlow?.shared_status === "active";

   const formInitialValues = {
      ...initialValues,
      notes: selectedTriggerProps?.notes || "",
      schedules: normalizeScheduleIds(selectedTriggerProps?.schedules)
   };

   const handleChangeTab = (event, newValue) => {
      setTabValue(newValue);
   };

   const updateStoreAndSaveFlow = (triggerProps) => {
      const currentTrigger = { ...selectedTrigger };
      currentTrigger.properties = { ...currentTrigger.properties, ...triggerProps };
      dispatch(updateContextTrigger(currentTrigger.label, currentSavedFlow?.name, triggerProps));
      dispatch(updateFlowTrigger(currentTrigger));
      dispatch(clearSelectedTrigger());
      autoSaveFlow(reactFlowInstance);
   };

   const handleSaveSubmit = async (values) => {
      if (isFlowActive || !allowEditWorkflow) return;

      const { schedules, notes } = values;

      const oldSchedules = originalScheduleIdsRef.current;
      const nextSchedules = normalizeScheduleIds(schedules);
      const newSchedules = nextSchedules.filter((id) => !oldSchedules.includes(id));
      const removedSchedules = oldSchedules.filter((id) => !nextSchedules.includes(id));

      const triggerProps = {
         schedules: nextSchedules,
         notes
      };

      const scheduleByFlowResponse = await insertScheduleByFlow(
         currentSavedFlow?.tenant_id,
         currentSavedFlow?.id,
         newSchedules,
         removedSchedules
      );

      if (!scheduleByFlowResponse?.isSuccessful) {
         toast.error("Failed to update schedule dependencies");
         setSubmitting(false);
         return;
      }

      if (newSchedules.length) {
         const createDependenciesResponse = await createResourceDependencies({
            tenantId: currentSavedFlow.tenant_id,
            dependencies: newSchedules.map((scheduleId) => ({
               sourceId: scheduleId,
               targetId: currentSavedFlow.id,
               sourceResourceType: "schedule",
               targetResourceType: "flow",
               contextId: `${scheduleId}:${currentSavedFlow.id}`
            }))
         });

         if (!createDependenciesResponse?.isSuccessful) {
            toast.error("Failed to create schedule dependencies");
            setSubmitting(false);
            return;
         }
      }

      if (removedSchedules.length) {
         const deleteDependenciesResponse = await deleteResourceDependencies({
            tenantId: currentSavedFlow.tenant_id,
            dependencies: removedSchedules.map((scheduleId) => ({
               sourceId: scheduleId,
               targetId: currentSavedFlow.id,
               sourceResourceType: "schedule",
               targetResourceType: "flow",
               contextId: `${scheduleId}:${currentSavedFlow.id}`
            }))
         });

         if (!deleteDependenciesResponse?.isSuccessful) {
            toast.error("Failed to delete schedule dependencies");
            setSubmitting(false);
            return;
         }
      }

      updateStoreAndSaveFlow(triggerProps);
      originalScheduleIdsRef.current = [...nextSchedules];

      setSubmitting(false);
      // closeForm();
   };

   const handleSubmit = (event) => {
      event.preventDefault();
      formRef.current.submitForm();
   };

   const handleOpenDiscardDialog = () => {
      dispatch(openDiscardDialog());
   };

   const checkDirtyAndClose = () => {
      if (formRef.current.dirty) {
         handleOpenDiscardDialog();
         return;
      }
      closeForm();
   };

   const closeForm = () => {
      handleGoBackToStartNodePanel();
   };

   const generateScheduleOptions = () => {
      const ownedSchedulesOptions = ownedSharedSchedules
         .map((schedule) => {
            const startDate = format(new Date(schedule.start), "MM/dd/yyyy HH:mm");
            const endDate = format(new Date(schedule.end), "MM/dd/yyyy HH:mm");

            return {
               label: schedule.title,
               subLabel: `From ${startDate} to ${endDate}`,
               value: schedule.id
            };
         })
         .sort((first, second) => (first.label || "").localeCompare(second.label || ""));
      return ownedSchedulesOptions;
   };

   const resetForm = () => {
      formRef.current?.resetForm();
   };

   useEffect(() => {
      if (showTriggerPropertiesPanel) {
         setTabValue(0);
         originalScheduleIdsRef.current = normalizeScheduleIds(
            selectedTriggerProps?.schedules
         );
      } else {
         resetForm();
      }
   }, [showTriggerPropertiesPanel, selectedTrigger?.label]);

   return (
      <>
         <TriggerFormWrapper onSubmit={handleSubmit} onCancel={checkDirtyAndClose} submitting={submitting} disableSubmit={isFlowActive || !allowEditWorkflow} tabValue={tabValue}>
            <Formik initialValues={formInitialValues} onSubmit={handleSaveSubmit} validationSchema={validations} innerRef={formRef} enableReinitialize>
               {({ values, errors, touched }) => {
                  const scheduleOptions = generateScheduleOptions();
                  const scheduleValues = scheduleOptions.filter((option) => values.schedules.includes(option.value));
                  return (
                     <Form id={formId} autoComplete="off">
                        <FormTabs tabValue={tabValue} onChange={handleChangeTab} />
                        <Box sx={{ padding: "8px 4px 8px 4px" }}>
                           <TabPanel value={tabValue} index={0}>
                              <FormField
                                 type={schedules.type}
                                 label={schedules.label}
                                 name={schedules.name}
                                 errors={errors.schedules && touched.schedules}
                                 placeholder=""
                                 options={scheduleOptions}
                                 isDisabled={isFlowActive || !allowEditWorkflow}
                                 value={scheduleValues || []}
                                 isMulti
                              />
                           </TabPanel>
                           <TabPanel value={tabValue} index={1}>
                              <FormField type={notes.type} label={notes.label} name={notes.name} value={values.notes} multiline rows={5} disabled={isFlowActive || !allowEditWorkflow} />
                           </TabPanel>
                        </Box>
                     </Form>
                  );
               }}
            </Formik>
            <DiscardChangesDialog onAccept={closeForm} />
         </TriggerFormWrapper>
         <TabPanel value={tabValue} index={2}>
            <Comments tabValue={tabValue} node_id={selectedTrigger.type} closeForm={closeForm} />
         </TabPanel>
      </>
   );
};

export default ScheduleTriggerForm;
